import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import AgeGate from "@/components/AgeGate";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartSheet from "@/components/CartSheet";
import PromoModal from "@/components/PromoModal";
import SupportWidget from "@/components/SupportWidget";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import About from "@/pages/About";
import CloneCare from "@/pages/CloneCare";
import COA from "@/pages/COA";
import Track from "@/pages/Track";
import WeeklyDrop from "@/pages/WeeklyDrop";
import CloneCategory from "@/pages/CloneCategory";
import AdminDashboard from "@/pages/AdminDashboard";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to={`/login?redirect=${location.pathname}`} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function ReferralCapture() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref && !localStorage.getItem("dg_ref")) {
      localStorage.setItem("dg_ref", ref.trim().toUpperCase());
    }
  }, [location.search]);
  return null;
}

function Layout({ children }) {
  return (
    <div className="grain min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 relative z-10">{children}</main>
      <Footer />
      <CartSheet />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ReferralCapture />
            <AgeGate />
            <PromoModal />
            <SupportWidget />
            <Toaster theme="dark" position="bottom-right" richColors />
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/clones/category/:slug" element={<CloneCategory />} />
                <Route path="/shop/:category" element={<Shop />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/clone-care" element={<CloneCare />} />
                <Route path="/coa" element={<COA />} />
                <Route path="/track" element={<Track />} />
                <Route path="/track/:invoice" element={<Track />} />
                <Route path="/weekly-drop" element={<WeeklyDrop />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/account" element={<Protected><Account /></Protected>} />
                <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
